import clsx from 'clsx';
import { forwardRef } from 'react';

/**
 * Card - Ana card bileşeni
 * Farklı varyasyonlar için: CardStats, CardAction, CardCompact, CardFeature
 */

// ============================================
// 1. BASE CARD - En temel card
// ============================================
export const Card = forwardRef(function Card({
    children,
    className = '',
    padding = 'md',
    hover = false,
    onClick,
    ...props
}, ref) {
    const paddingClasses = {
        none: '',
        sm: 'p-3',
        md: 'p-4',
        lg: 'p-6',
        xl: 'p-8',
    };

    return (
        <div
            ref={ref}
            className={clsx(
                'bg-bg-secondary rounded-xl border border-border-light',
                paddingClasses[padding],
                hover && 'hover:shadow-lg hover:border-primary/30 transition-all cursor-pointer',
                onClick && 'cursor-pointer',
                className
            )}
            onClick={onClick}
            {...props}
        >
            {children}
        </div>
    );
});

// ============================================
// 2. CARD STATS - İstatistik kartı (Dashboard için)
// ============================================
export function CardStats({
    title,
    value,
    subtitle,
    icon: Icon,
    trend,
    trendValue,
    color = 'primary',
    size = 'md',
    className = '',
    onClick,
}) {
    const colorClasses = {
        primary: {
            icon: 'bg-primary-100 text-primary',
            trend: trend === 'up' ? 'text-success' : 'text-error',
        },
        secondary: {
            icon: 'bg-secondary-100 text-secondary',
            trend: trend === 'up' ? 'text-success' : 'text-error',
        },
        tertiary: {
            icon: 'bg-tertiary-100 text-tertiary',
            trend: trend === 'up' ? 'text-success' : 'text-error',
        },
        success: {
            icon: 'bg-success-100 text-success',
            trend: trend === 'up' ? 'text-success' : 'text-error',
        },
        warning: {
            icon: 'bg-warning-100 text-warning',
            trend: trend === 'up' ? 'text-success' : 'text-error',
        },
        error: {
            icon: 'bg-error-100 text-error',
            trend: trend === 'up' ? 'text-success' : 'text-error',
        },
        info: {
            icon: 'bg-info-100 text-info',
            trend: trend === 'up' ? 'text-success' : 'text-error',
        },
    };

    const sizeClasses = {
        sm: {
            container: 'p-3',
            icon: 'w-8 h-8',
            iconSize: 16,
            title: 'text-xs',
            value: 'text-base sm:text-lg font-bold',
            subtitle: 'text-2xs',
        },
        md: {
            container: 'p-3 sm:p-4',
            icon: 'w-8 h-8 sm:w-10 sm:h-10',
            iconSize: 18,
            title: 'text-xs sm:text-sm',
            value: 'text-lg sm:text-xl lg:text-2xl font-bold',
            subtitle: 'text-xs',
        },
        lg: {
            container: 'p-4 sm:p-5',
            icon: 'w-10 h-10 sm:w-12 sm:h-12',
            iconSize: 22,
            title: 'text-sm sm:text-base',
            value: 'text-xl sm:text-2xl lg:text-3xl font-bold',
            subtitle: 'text-xs sm:text-sm',
        },
    };

    const sizeConfig = sizeClasses[size];
    const colorConfig = colorClasses[color];

    return (
        <div
            className={clsx(
                'bg-bg-secondary rounded-xl border border-border-light',
                sizeConfig.container,
                onClick && 'hover:shadow-lg hover:border-primary/30 transition-all cursor-pointer',
                className
            )}
            onClick={onClick}
        >
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <p className={clsx('text-text-tertiary', sizeConfig.title)}>{title}</p>
                    <p className={clsx('text-text-primary mt-1', sizeConfig.value)}>{value}</p>
                    {subtitle && (
                        <p className={clsx('text-text-muted mt-1', sizeConfig.subtitle)}>{subtitle}</p>
                    )}
                    {trendValue && (
                        <p className={clsx('mt-2 flex items-center gap-1', sizeConfig.subtitle, colorConfig.trend)}>
                            {trend === 'up' ? '↑' : '↓'} {trendValue}
                        </p>
                    )}
                </div>
                {Icon && (
                    <div className={clsx('rounded-lg flex items-center justify-center', sizeConfig.icon, colorConfig.icon)}>
                        <Icon size={sizeConfig.iconSize} />
                    </div>
                )}
            </div>
        </div>
    );
}

// ============================================
// 3. CARD ACTION - Aksiyon kartı (Hızlı işlem butonları)
// ============================================
export function CardAction({
    title,
    subtitle,
    icon: Icon,
    color = 'primary',
    onClick,
    disabled = false,
    className = '',
}) {
    const colorClasses = {
        primary: 'bg-primary hover:bg-primary-dark',
        secondary: 'bg-secondary hover:bg-secondary-dark',
        tertiary: 'bg-tertiary hover:bg-tertiary-dark',
        success: 'bg-success hover:bg-success-dark',
        warning: 'bg-warning hover:bg-warning-dark',
        error: 'bg-error hover:bg-error-dark',
        info: 'bg-info hover:bg-info-dark',
    };

    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={clsx(
                'w-full rounded-xl text-white text-left p-4 sm:p-5 transition-all min-h-[72px]',
                'hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]',
                colorClasses[color],
                disabled && 'opacity-50 cursor-not-allowed',
                className
            )}
        >
            <div className="flex items-center gap-3 sm:gap-4">
                {Icon && (
                    <div className="w-11 h-11 sm:w-12 sm:h-12 bg-white/20 rounded-lg flex items-center justify-center shrink-0">
                        <Icon size={24} />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <p className="font-semibold text-base sm:text-lg truncate">{title}</p>
                    {subtitle && <p className="text-white/80 text-xs sm:text-sm mt-0.5 truncate">{subtitle}</p>}
                </div>
            </div>
        </button>
    );
}

// ============================================
// 4. CARD COMPACT - Kompakt kart (Liste öğesi gibi)
// ============================================
export function CardCompact({
    title,
    subtitle,
    value,
    icon: Icon,
    status,
    statusColor = 'primary',
    onClick,
    className = '',
}) {
    const statusColors = {
        primary: 'bg-primary-100 text-primary',
        secondary: 'bg-secondary-100 text-secondary',
        tertiary: 'bg-tertiary-100 text-tertiary',
        success: 'bg-success-100 text-success',
        warning: 'bg-warning-100 text-warning',
        error: 'bg-error-100 text-error',
        info: 'bg-info-100 text-info',
    };

    return (
        <div
            className={clsx(
                'bg-bg-secondary rounded-lg border border-border-light p-3 min-h-[56px]',
                'hover:border-primary/30 transition-all',
                onClick && 'cursor-pointer active:scale-[0.99]',
                className
            )}
            onClick={onClick}
        >
            <div className="flex items-center gap-3">
                {Icon && (
                    <div className="w-10 h-10 bg-bg-hover rounded-lg flex items-center justify-center text-text-tertiary shrink-0">
                        <Icon size={20} />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <p className="font-medium text-text-primary truncate text-sm sm:text-base">{title}</p>
                    {subtitle && <p className="text-xs sm:text-sm text-text-tertiary truncate">{subtitle}</p>}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                    {value && <p className="font-semibold text-text-primary text-sm sm:text-base">{value}</p>}
                    {status && (
                        <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', statusColors[statusColor])}>
                            {status}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

// ============================================
// 5. CARD FEATURE - Özellik kartı (Büyük, detaylı)
// ============================================
export function CardFeature({
    title,
    description,
    icon: Icon,
    color = 'primary',
    badge,
    footer,
    children,
    className = '',
    onClick,
}) {
    const colorClasses = {
        primary: 'from-primary to-primary-dark',
        secondary: 'from-secondary to-secondary-dark',
        tertiary: 'from-tertiary to-tertiary-dark',
        success: 'from-success to-success-dark',
        warning: 'from-warning to-warning-dark',
        error: 'from-error to-error-dark',
        info: 'from-info to-info-dark',
    };

    return (
        <div
            className={clsx(
                'bg-bg-secondary rounded-2xl border border-border-light overflow-hidden',
                'hover:shadow-xl transition-all',
                onClick && 'cursor-pointer',
                className
            )}
            onClick={onClick}
        >
            {/* Header with gradient */}
            <div className={clsx('bg-gradient-to-r p-5 text-white', colorClasses[color])}>
                <div className="flex items-start justify-between">
                    <div>
                        {badge && (
                            <span className="inline-block px-2 py-1 rounded-full bg-white/20 text-xs font-medium mb-2">
                                {badge}
                            </span>
                        )}
                        <h3 className="text-xl font-bold">{title}</h3>
                        {description && <p className="text-white/80 mt-1 text-sm">{description}</p>}
                    </div>
                    {Icon && (
                        <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                            <Icon size={28} />
                        </div>
                    )}
                </div>
            </div>

            {/* Content */}
            {children && (
                <div className="p-5">
                    {children}
                </div>
            )}

            {/* Footer */}
            {footer && (
                <div className="px-5 py-3 bg-bg-tertiary border-t border-border-light">
                    {footer}
                </div>
            )}
        </div>
    );
}

// Default export
export default Card;
